import { ChevronRight, X } from 'lucide-react'
import { useSection } from '@/context/SectionContext'
import { SECTION_DEFINITIONS, type SectionKey } from '../sections'
import { cn, cardClassName } from '@jira-clone/ui'

interface SectionCardsProps {
  availableSections: SectionKey[]
  onSelect?: (sectionKey: SectionKey) => void
  hideHeader?: boolean
}

const HR_STYLE = {
  iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400',
  tag: 'People & Staff',
  bottomBar: 'from-emerald-500 to-teal-400',
  corner: 'bg-emerald-400/15 dark:bg-emerald-400/10',
  link: 'text-emerald-600 dark:text-emerald-400',
}

const SECTION_STYLES: Record<
  SectionKey,
  {
    iconBg: string
    tag: string
    /** Bar along the bottom edge (clipped to the card's rounded corners). */
    bottomBar: string
    /** Faint diagonal tint in the top-right corner. */
    corner: string
    /** Accent colour for the "Open workspace" link. */
    link: string
  }
> = {
  company: {
    iconBg: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-400',
    tag: 'All Modules',
    bottomBar: 'from-cyan-500 to-sky-400',
    corner: 'bg-cyan-400/15 dark:bg-cyan-400/10',
    link: 'text-cyan-600 dark:text-cyan-400',
  },
  sales: {
    iconBg: 'bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400',
    tag: 'CRM & Pipeline',
    bottomBar: 'from-rose-500 to-pink-400',
    corner: 'bg-rose-400/15 dark:bg-rose-400/10',
    link: 'text-rose-600 dark:text-rose-400',
  },
  accounts: {
    iconBg: 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400',
    tag: 'Finance & Invoicing',
    bottomBar: 'from-amber-500 to-yellow-400',
    corner: 'bg-amber-400/20 dark:bg-amber-400/10',
    link: 'text-amber-700 dark:text-amber-400',
  },
  projects: {
    iconBg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400',
    tag: 'Spaces & Delivery',
    bottomBar: 'from-indigo-500 to-blue-400',
    corner: 'bg-indigo-400/15 dark:bg-indigo-400/10',
    link: 'text-indigo-600 dark:text-indigo-400',
  },
  hr: HR_STYLE,
  // Alias of `hr`; not shown in the workspace picker.
  people: HR_STYLE,
}

export default function SectionCards({ availableSections, onSelect, hideHeader }: SectionCardsProps) {
  const { selectedSection, setSelectedSection } = useSection()

  if (availableSections.length === 0) return null

  return (
    <div className="w-full" role="region" aria-label="Dashboard sections">
      {/* Header (optional) */}
      {!hideHeader && (
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Select Department
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Choose a department to filter your dashboard & sidebar navigation.
            </p>
          </div>

          {selectedSection && (
            <button
              type="button"
              onClick={() => setSelectedSection(null)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-border"
            >
              <X className="w-3.5 h-3.5" />
              <span>Reset Selection</span>
            </button>
          )}
        </div>
      )}

      {/* Natural, clean 5-Card Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5 animate-fade-in">
        {availableSections.map((sectionKey) => {
          const section = SECTION_DEFINITIONS[sectionKey]
          const isSelected = selectedSection === sectionKey
          const Icon = section.icon
          const style = SECTION_STYLES[sectionKey] || SECTION_STYLES.company

          const handleCardClick = () => {
            if (onSelect) {
              onSelect(sectionKey)
            } else {
              setSelectedSection(isSelected ? null : sectionKey)
            }
          }

          return (
            <div
              key={sectionKey}
              onClick={handleCardClick}
              tabIndex={0}
              role="button"
              aria-pressed={isSelected}
              aria-label={isSelected ? `Deselect ${section.label}` : `Filter by ${section.label}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleCardClick()
                }
              }}
              className={cn(
                cardClassName({ interactive: true }),
                'group relative flex flex-col justify-between p-5 pb-6 text-left select-none overflow-hidden',
                'transition-all duration-200 ease-out transform-gpu min-h-[200px]',
                'hover:-translate-y-1 hover:border-border',
                'active:scale-[0.98] active:translate-y-0',
                isSelected && 'border-primary ring-2 ring-primary/20'
              )}
            >
              {/* Faint diagonal tint, top-right corner */}
              <div
                aria-hidden="true"
                className={cn('pointer-events-none absolute right-0 top-0 h-24 w-24 [clip-path:polygon(25%_0,100%_0,100%_75%)]', style.corner)}
              />
              {/* Accent bar along the bottom edge */}
              <div
                aria-hidden="true"
                className={cn('absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r', style.bottomBar)}
              />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={cn(
                      'w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-105',
                      style.iconBg
                    )}
                  >
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>

                  <span className="text-[10px] sm:text-[11px] font-semibold text-muted-foreground group-hover:text-foreground transition-colors duration-200">
                    {style.tag}
                  </span>
                </div>

                <h4 className="font-bold text-lg text-foreground tracking-tight transition-colors duration-200">
                  {section.label}
                </h4>

                <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">
                  {section.description}
                </p>
              </div>

              <div className="mt-5 pt-3 border-t border-border/50 flex items-center justify-between text-xs">
                <span className={cn('font-semibold', style.link)}>Open workspace</span>
                <ChevronRight className={cn('w-4 h-4 transition-transform duration-200 group-hover:translate-x-1', style.link)} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
